import { useEffect } from 'react';
import {
  GeneralConfigurationType,
  defaultGeneralConfiguration,
} from '@consolidocs-ui/types';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { useLazyGetGeneralConfigurationByNameQuery } from '@consolidocs-ui/data-access/GeneralConfigurationApi';
import {
  setDefaultSettings,
  setLoading,
  setError,
} from './generalConfigurationSlice';
import { GeneralConfigurationState } from './generalConfigurationSlice';

/** Minimal slice of the store this hook depends on, so it stays app-agnostic. */
type WithGeneralConfiguration = { generalConfiguration: GeneralConfigurationState };

interface UseGeneralConfigurationOptions {
  defaultSettingsName?: string;
}

export const useGeneralConfiguration = (
  options: UseGeneralConfigurationOptions = {}
) => {
  const { defaultSettingsName = 'default-settings' } = options;

  const dispatch = useDispatch();
  const auth = useAuth();

  const { defaultSettings, isLoading, error } = useSelector(
    (state: WithGeneralConfiguration) => state.generalConfiguration
  );

  const [triggerGetByName] = useLazyGetGeneralConfigurationByNameQuery();

  const loadConfiguration = async () => {
    if (!auth.isAuthenticated) {
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const defaultSettingsResult = await triggerGetByName(
        defaultSettingsName
      ).unwrap();
      dispatch(setDefaultSettings(defaultSettingsResult ?? null));
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || err?.message || 'Failed to load configuration';
      // Fall back to the built-in defaults, wrapped in the record shape the
      // slice and its selectors read (`defaultSettings.configuration[key]`).
      const fallback: GeneralConfigurationType = {
        id: '',
        name: defaultSettingsName,
        configuration: { ...defaultGeneralConfiguration },
        created_at: '',
        updated_at: '',
      };
      dispatch(setDefaultSettings(fallback));
      dispatch(setError(errorMessage));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      loadConfiguration();
    }
  }, [auth.isAuthenticated, auth.user?.profile?.sub]); // Only depend on user ID to avoid unnecessary refetches

  return {
    defaultSettings,
    isLoading,
    error,
    refetch: loadConfiguration,
  };
};
